'use client'

import React, { useEffect, useRef, useState } from 'react'
import { usePathname, useRouter } from 'next/navigation'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { Timeout } from 'react-number-format/types/types'

import { useLocale } from '@rewardops/ui'

import { sortNotificationRibbonsByOrder } from '~/utils/sort-utils'
import { useApi } from '../hooks/use-api'

type NotificationRibbonLink = {
  to: string
  name: string
  isRelativeUrl: boolean
}

export type NotificationRibbonParams = {
  backgroundColor: string
  id: string
  text: string
  textColor: string
  link?: NotificationRibbonLink
  pagePaths?: string[]
  order?: number
}

const NotificationRibbon: React.FC = () => {
  const { api, tenant } = useApi()
  const { locale } = useLocale()
  const router = useRouter()
  const pathname = `${usePathname()}`?.split(locale)

  const intervalRef = useRef<Timeout | null>(null)

  const { notificationRibbonTimeLapse } = tenant

  const [pauseTransition, setPauseTransition] = useState<boolean>(false)
  const [notificationData, setNotificationData] = useState<
    NotificationRibbonParams[]
  >([])
  const INITIAL_NOTIFICATION_INDEX = 0
  const [currentNotification, setCurrentNotification] = useState<number>(
    INITIAL_NOTIFICATION_INDEX,
  )

  const currentPage = pathname?.[pathname?.length - 1] ?? ''

  const {
    data,
    isLoading,
  }: { data: NotificationRibbonParams[] | undefined; isLoading: boolean } =
    api.contentful.getByTypeAndFilterByField.useQuery({
      type: 'notificationRibbon',
      language: locale ?? tenant?.locale?.default,
      fields: [
        {
          key: 'id',
          value: 'notification-ribbon',
        },
      ],
    })

  const {
    data: pageSpecificPageRibbons,
    isLoading: pageSpecificPageRibbonsLoading,
  }: { data: NotificationRibbonParams[] | undefined; isLoading: boolean } =
    api.contentful.getByTypeAndFilterByField.useQuery({
      type: 'notificationRibbon',
      language: locale ?? tenant?.locale?.default,
      exists: ['pagePaths'],
    })

  useEffect(() => {
    if (!isLoading && !pageSpecificPageRibbonsLoading) {
      if (pageSpecificPageRibbons?.length) {
        const currentPageRibbon = pageSpecificPageRibbons?.filter((ribbon) =>
          ribbon?.pagePaths?.includes(Boolean(currentPage) ? currentPage : '/'),
        )
        if (currentPageRibbon?.length) {
          setNotificationData([
            ...sortNotificationRibbonsByOrder(currentPageRibbon),
            ...sortNotificationRibbonsByOrder(data ?? []),
          ])
          return
        }
      }

      if (data?.length) {
        setNotificationData(sortNotificationRibbonsByOrder(data))
      } else {
        setNotificationData([])
      }
    }
  }, [isLoading, pageSpecificPageRibbonsLoading, currentPage])

  useEffect(() => {
    if (notificationData?.length > 1 && !pauseTransition) {
      intervalRef.current = setInterval(
        goNext,
        notificationRibbonTimeLapse + 1000,
      )

      return () => clearInterval(Number(intervalRef?.current))
    }
  }, [notificationData?.length, currentNotification, pauseTransition])

  if (
    isLoading ||
    pageSpecificPageRibbonsLoading ||
    !notificationData?.length
  ) {
    return null
  }

  const onClick = (link: NotificationRibbonLink) => {
    if (link?.isRelativeUrl) {
      router.push(link?.to)
    } else {
      window?.open(link?.to, '_blank')
    }
  }

  const goNext = () => {
    if (currentNotification == notificationData?.length - 1) {
      setCurrentNotification(INITIAL_NOTIFICATION_INDEX)
      return
    }

    setCurrentNotification((prev) => prev + 1)
  }

  const goPrev = () => {
    if (currentNotification == INITIAL_NOTIFICATION_INDEX) {
      setCurrentNotification(notificationData?.length - 1)
      return
    }

    setCurrentNotification((prev) => prev - 1)
  }

  const onMouseEnterOrKeyPress = () => {
    if (notificationData?.length <= 1) return
    setPauseTransition(true)
    if (intervalRef.current) {
      clearInterval(Number(intervalRef?.current))
    }
  }

  const onMouseLeaveOrKeyUp = () => {
    if (notificationData?.length <= 1) return
    setPauseTransition(false)
  }

  return (
    <div
      className="flex overflow-hidden"
      onMouseOver={onMouseEnterOrKeyPress}
      onFocus={onMouseEnterOrKeyPress}
      onKeyDown={onMouseEnterOrKeyPress}
      onMouseOut={onMouseLeaveOrKeyUp}
      onBlur={onMouseLeaveOrKeyUp}
      onKeyUp={onMouseLeaveOrKeyUp}
    >
      {notificationData?.map(
        ({ id, backgroundColor, link, text, textColor }, index) => (
          <div
            id={`notification-ribbon`}
            key={`${id}-${index}`}
            className="flex h-auto w-full shrink-0 items-center justify-center px-2 py-3 text-sm transition duration-1000 ease-out"
            style={{
              backgroundColor,
              color: textColor ? textColor : 'unset',
              transform: `translateX(-${currentNotification * 100}%)`,
            }}
          >
            {notificationData?.length > 1 ? (
              <button className="mr-auto cursor-pointer px-4" onClick={goPrev}>
                <ChevronLeft className="text-white" size={24} />
              </button>
            ) : null}
            {text}&nbsp;
            {link ? (
              <span
                className="cursor-pointer text-sm font-semibold underline"
                onClick={() => onClick(link)}
              >
                {link?.name}
              </span>
            ) : null}
            {notificationData?.length > 1 ? (
              <button className="ml-auto cursor-pointer px-4" onClick={goNext}>
                <ChevronRight className="text-white" size={24} />
              </button>
            ) : null}
          </div>
        ),
      )}
    </div>
  )
}

export { NotificationRibbon }
